import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { DEAL_STAGES } from "@/lib/deal-stages";

interface CreateDealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  orgId: string;
  familyId?: string;
  clientId: string;
  contacts: Array<{ id: string; full_name: string; organization?: string | null }>;
}

export function CreateDealDialog({ 
  open, 
  onOpenChange, 
  onSuccess, 
  orgId, 
  familyId, 
  clientId,
  contacts 
}: CreateDealDialogProps) {
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    stage: "sourcing",
    asset_class: "",
    size: "",
    contact_id: undefined as string | undefined,
    notes: "",
    next_steps: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Deal name is required");
      return;
    }

    setCreating(true);
    try {
      const { error } = await supabase
        .from("deals")
        .insert({
          org_id: orgId,
          family_id: familyId || null,
          client_id: clientId,
          name: formData.name.trim(),
          stage: formData.stage,
          asset_class: formData.asset_class || null,
          size: formData.size ? parseFloat(formData.size) : null,
          contact_id: formData.contact_id || null,
          notes: formData.notes.trim() || null,
          next_steps: formData.next_steps.trim() || null,
        });

      if (error) throw error;

      toast.success("Deal created successfully");

      // Reset form
      setFormData({
        name: "",
        stage: "sourcing",
        asset_class: "",
        size: "",
        contact_id: undefined,
        notes: "",
        next_steps: "",
      });

      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error("Error creating deal:", error);
      toast.error("Failed to create deal");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Deal</DialogTitle>
          <DialogDescription>
            Add a new investment opportunity to your pipeline
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Deal Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Deal Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="e.g., Vista Equity Partners Fund VIII"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          {/* Stage */}
          <div className="space-y-2">
            <Label htmlFor="stage">Stage</Label>
            <Select
              value={formData.stage}
              onValueChange={(value) => setFormData({ ...formData, stage: value })}
            >
              <SelectTrigger id="stage">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEAL_STAGES.map((stage) => (
                  <SelectItem key={stage.value} value={stage.value}>
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Asset Class */}
          <div className="space-y-2">
            <Label htmlFor="asset_class">Asset Class</Label>
            <Select
              value={formData.asset_class}
              onValueChange={(value) => setFormData({ ...formData, asset_class: value })}
            >
              <SelectTrigger id="asset_class">
                <SelectValue placeholder="Select asset class" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Private Equity">Private Equity</SelectItem>
                <SelectItem value="Venture Capital">Venture Capital</SelectItem>
                <SelectItem value="Real Estate">Real Estate</SelectItem>
                <SelectItem value="Credit">Credit</SelectItem>
                <SelectItem value="Infrastructure">Infrastructure</SelectItem>
                <SelectItem value="Hedge Fund">Hedge Fund</SelectItem>
                <SelectItem value="Public Equity">Public Equity</SelectItem>
                <SelectItem value="Fixed Income">Fixed Income</SelectItem>
                <SelectItem value="Co-Investment">Co-Investment</SelectItem>
                <SelectItem value="Direct Investment">Direct Investment</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Size */}
          <div className="space-y-2">
            <Label htmlFor="size">Size ($)</Label>
            <Input
              id="size"
              type="number"
              placeholder="Deal size in USD"
              value={formData.size}
              onChange={(e) => setFormData({ ...formData, size: e.target.value })}
            />
          </div>

          {/* Contact */}
          <div className="space-y-2">
            <Label htmlFor="contact">Contact</Label>
            <Select
              value={formData.contact_id}
              onValueChange={(value) => setFormData({ ...formData, contact_id: value })}
            >
              <SelectTrigger id="contact">
                <SelectValue placeholder="Select contact" />
              </SelectTrigger>
              <SelectContent>
                {contacts.map((contact) => (
                  <SelectItem key={contact.id} value={contact.id}>
                    {contact.full_name} {contact.organization && `(${contact.organization})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Investment thesis, key highlights, due diligence notes..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
            />
          </div>

          {/* Next Steps */}
          <div className="space-y-2">
            <Label htmlFor="next_steps">Next Steps</Label>
            <Textarea
              id="next_steps"
              placeholder="What needs to happen next..."
              value={formData.next_steps}
              onChange={(e) => setFormData({ ...formData, next_steps: e.target.value })}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Deal
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
